import {ParseResult, Schema} from "effect";

// ── Bare user UID (unbranded Firebase auth UID) ────────────────────────

/**
 * A bare user UID is the raw Firebase auth UID with no principal prefix
 * (minimum length 2). It is the form stored in Firestore auth/membership keys
 * and carried by `currentUser.uid` — the persistence-layer sink type.
 *
 * Branded so a {@link PrefixedUserUid} (`usr_…`) or a plain `string` cannot be
 * assigned where a bare UID is required; mismatches surface at compile time
 * instead of as silent string inequality.
 */
const BareUserUidSchema = Schema.String.pipe(Schema.minLength(2), Schema.brand("BareUserUid"));

export type BareUserUid = typeof BareUserUidSchema.Type;

/**
 * Type guard — decides whether a value is a BareUserUid.
 */
export const isBareUserUid = Schema.is(BareUserUidSchema);

/**
 * Deprecated bare-UID alias retained for backward compatibility.
 *
 * @deprecated Use {@link BareUserUid} (the unbranded form) or
 * {@link PrefixedUserUid} (`usr_` form) explicitly.
 */
export type LegacyUserUid = BareUserUid;

/**
 * @deprecated Use {@link isBareUserUid}.
 */
export const isLegacyUserUid = isBareUserUid;

// ── Prefixed user UID (new canonical form) ─────────────────────────────

/**
 * A prefixed user UID is a string that starts with "usr_" followed by the
 * Firebase auth UID.
 *
 * Effect Schema equivalent of the legacy zod `PrefixedUserUid` declared in
 * `@buildwithflux/identifiers`. Acceptance semantics are identical (see
 * runtime parity tests in `UserUid.test.ts`).
 */
const PrefixedUserUidSchema = Schema.TemplateLiteral("usr_", Schema.String).pipe(Schema.brand("PrefixedUserUid"));

export type PrefixedUserUid = typeof PrefixedUserUidSchema.Type;

/**
 * Type guard — decides whether a string is a branded PrefixedUserUid.
 */
export const isPrefixedUserUid = Schema.is(PrefixedUserUidSchema);

// ── Canonical inverse pair: Bare ↔ Prefixed ────────────────────────────

/**
 * `Schema.transformOrFail` bundling the inverse pair
 * `BareUserUid ↔ PrefixedUserUid`.
 *
 * Bundling both directions in a single carrier prevents the two halves from
 * drifting and lets callers compose the conversion into Effect Schema
 * pipelines (e.g. `Schema.encode`, `Schema.decode`, struct fields) instead of
 * calling matched standalone helpers.
 *
 * - decode (Bare → Prefixed): prepend `"usr_"` to a `BareUserUid`. Always
 *   succeeds for any value the source schema accepts.
 * - encode (Prefixed → Bare): strip the `"usr_"` prefix from a
 *   `PrefixedUserUid`. The stripped value must satisfy `BareUserUid`'s
 *   `minLength(2)` invariant; if it does not (e.g. encoding `"usr_a"`), the
 *   transform fails with a typed `ParseResult.Type` issue.
 */
export const BareUserUidToPrefixedUserUid = Schema.transformOrFail(
    BareUserUidSchema,
    PrefixedUserUidSchema,
    {
        strict: true,
        decode: (bare) => ParseResult.succeed(`usr_${bare}` as PrefixedUserUid),
        encode: (prefixed, options, ast) => {
            const stripped = prefixed.slice("usr_".length);
            // Delegate to the source schema's decoder so any future constraint
            // added to `BareUserUidSchema` (length, character class, etc.) is
            // automatically enforced on the encode direction without duplication.
            return ParseResult.mapError(
                Schema.decode(BareUserUidSchema)(stripped, options),
                (issue) =>
                    new ParseResult.Type(
                        ast,
                        prefixed,
                        `Stripped UID "${stripped}" does not satisfy BareUserUid: ${issue.message}`,
                    ),
            );
        },
    },
);

/**
 * @deprecated Use {@link BareUserUidToPrefixedUserUid}.
 */
export const LegacyUserUidToPrefixedUserUid = BareUserUidToPrefixedUserUid;

// ── Conversions (idempotent convenience helpers) ───────────────────────

/**
 * Convert any user UID string to a {@link PrefixedUserUid}. Idempotent — if
 * the value already has the "usr_" prefix, it is returned as-is.
 *
 * Idempotent semantics let callers safely re-prefix mixed inputs at API
 * boundaries; the canonical strict inverse is
 * {@link BareUserUidToPrefixedUserUid}.
 */
export function toPrefixedUserUid(uid: string): PrefixedUserUid {
    return (isPrefixedUserUid(uid) ? uid : `usr_${uid}`) as PrefixedUserUid;
}

/**
 * Extract the bare Firebase auth UID from a {@link PrefixedUserUid} by
 * stripping the "usr_" prefix. If the value does not have the prefix, it is
 * returned as-is.
 *
 * Returns a plain `string` (not a branded {@link BareUserUid}) to preserve 1:1
 * parity with the legacy zod helper. Stripping the prefix can produce a value
 * shorter than the brand's `minLength(2)` invariant (e.g. `"usr_a"` → `"a"`),
 * so callers that need a branded `BareUserUid` must validate via
 * {@link isBareUserUid}, `BareUserUid.schema`, or use
 * {@link BareUserUidToPrefixedUserUid}'s `encode` direction (which surfaces the
 * violation as a typed parse error).
 */
export function parseUserUidFromPrefixed(uid: string): string {
    return uid.startsWith("usr_") ? uid.slice("usr_".length) : uid;
}

// ── Divergent-form tolerance (canonical helpers) ───────────────────────

/**
 * Both canonical forms of a user UID — bare and `usr_`-prefixed — for the
 * "tolerate either form" pattern (emit/lookup both so a reader matches
 * whichever form a writer stored). The forms always differ, so never duplicate.
 * See `domain/identifiers/README.md` for the divergence rationale.
 */
export function userUidForms(uid: string): readonly [bare: string, prefixed: PrefixedUserUid] {
    const bare = parseUserUidFromPrefixed(uid);
    return [bare, toPrefixedUserUid(bare)] as const;
}

/**
 * Read a record keyed by user UID tolerating either form: prefixed (canonical)
 * key first, then bare. Centralizes the `record[prefixed] ?? record[bare]` idiom.
 *
 * Uses key presence (not value nullishness) so an explicit `null`/`undefined`
 * stored under the canonical prefixed key wins over a stale bare-key value.
 */
export function getByUserUid<T>(record: Readonly<{[key: string]: T}>, uid: string): T | undefined {
    const [bare, prefixed] = userUidForms(uid);
    return Object.prototype.hasOwnProperty.call(record, prefixed) ? record[prefixed] : record[bare];
}

// ── Companion objects ──────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const BareUserUid = {
    schema: BareUserUidSchema,
    isBareUserUid,
    /**
     * @deprecated Use {@link isBareUserUid}.
     */
    isLegacyUserUid,
} as const;

/**
 * @deprecated Use {@link BareUserUid}.
 */
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const LegacyUserUid = BareUserUid;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PrefixedUserUid = {
    schema: PrefixedUserUidSchema,
    isPrefixedUserUid,
    toPrefixedUserUid,
    parseUserUidFromPrefixed,
    userUidForms,
    getByUserUid,
    /**
     * Canonical strict inverse — see {@link BareUserUidToPrefixedUserUid}.
     */
    fromBare: BareUserUidToPrefixedUserUid,
    /**
     * @deprecated Use {@link PrefixedUserUid.fromBare}.
     */
    fromLegacy: BareUserUidToPrefixedUserUid,
} as const;
